import { inject, Injectable } from '@angular/core';
import { DefinitionRestService } from 'app/core/services/rest/definition.rest.service';
import { Store } from '@ngrx/store';
import { RootState } from 'app/core/store/index';
import { safe } from 'app/core/util/util';
import { combineLatestWith, distinctUntilChanged, filter, map } from 'rxjs/operators';
import { BusyAction, BusyDoneAction } from 'app/core/store/ui/ui.actions';
import { Subject } from 'rxjs';
import { definitionsLoadAction, definitionsLoadFailAction, definitionsLoadSuccessAction } from 'app/core/store/definitions/definitions.actions';
import { logger } from '../../util/logger';
import { NotificationService } from '../../services/notification.service';
import { AzureMonitoringService } from '../../services/AzureMonitoringService';
import { AuthenticationService } from '../../authentication/authentication.service';

function stringifyError(error: Error): Object {
  const result = {
    errorStringified: null,
    errorName: null,
    errorStack: null,
    errorMessage: null,
  };
  try { result.errorStringified = JSON.stringify(error) } catch (_) { }
  try { result.errorMessage = error.message } catch (_) { }
  try { result.errorName = error.name } catch (_) { }
  try { result.errorStack = error.stack } catch (_) { }
  return result;
}

@Injectable({ providedIn: 'root' })
export class DefinitionsService {

  private authenticationService = inject(AuthenticationService);

  private loadDefinition = new Subject<string>();

  constructor(
    private store: Store<RootState>,
    private definitionRestService: DefinitionRestService,
    private notificationService: NotificationService,
    private azureMonitoringService: AzureMonitoringService,
  ) {
    this.listenToLoadRequest();
  }

  /**
   * Load defintions for given language
   * - only when authenticated
   * - load only once
   * - wait until previous load is finished
   * @param language
   */
  loadOnce(language: string): void {
    this.loadDefinition.next(language);
  }

  private listenToLoadRequest() {
    this.loadDefinition
      .pipe(
        combineLatestWith(this.authenticationService.authenticated$, this.store.select('definitions')),
        map(([language, isAuthenticated, definitions]) => {
          return {
            language: (language || '').toLowerCase(),
            loadedLanguage: ((definitions.loaded && definitions.language) || '').toLowerCase(),
            isAuthenticated,
            loaded: definitions.loaded,
            loading: definitions.loading,
            failed: definitions.failed,
          };
        }),
        distinctUntilChanged((previous, current) => {
          const isEqual =
            previous.loaded === current.loaded &&
            previous.loading === current.loading &&
            previous.failed === current.failed &&
            previous.isAuthenticated === current.isAuthenticated &&
            previous.language === current.language;
          return isEqual;
        }),
        filter(({ loading, isAuthenticated, language, loadedLanguage }) => isAuthenticated && !loading && language !== loadedLanguage),
        distinctUntilChanged((previous, current) => previous.language === current.language && previous.failed === current.failed)
      )
      .subscribe(async ({ language, failed }) => {
        if (!failed) {
          logger.info('will load definitions');
          this.store.dispatch(new BusyAction('definitions-load'));
          this.store.dispatch(definitionsLoadAction());
          const { result: definitions, error } = await safe(this.definitionRestService.getDefinitions(language));
          this.store.dispatch(new BusyDoneAction('definitions-load'));
          if (definitions) {
            this.store.dispatch(definitionsLoadSuccessAction({ definitions, language }));
          } else {
            this.azureMonitoringService.logException(error, { message: 'definitions load fail', ...stringifyError(error) });
            console.error(error);
            this.store.dispatch(definitionsLoadFailAction(error));
            this.notificationService.showModal({
              disableClose: true,
              type: 'error',
              // message: error?.failMessage,
              message: 'Failed to load the definitions',
            });
          }
        }
      });
  }
}

